import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  LayoutDashboard,
  FileText,
  LogOut,
  Search,
  AlertTriangle,
  MessageSquare,
  Menu,
  X,
  ChevronRight,
  Filter,
  Users,
  Download,
  FileDown,
  Settings,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import TrialExpiredScreen from "../components/TrialExpiredScreen";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmpresaCasos() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { user, empresa, logout } = useAuth();
  const [denuncias, setDenuncias] = useState([]);
  const [ouvidorias, setOuvidorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [tipoFilter, setTipoFilter] = useState(searchParams.get("tipo") || "all");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [lastBackendFilter, setLastBackendFilter] = useState(null); // Track if backend filter was used

  // Check subscription/trial status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!slug) return;
      try {
        const response = await axios.get(`${API_URL}/empresas/${slug}/subscription-status`);
        setSubscriptionStatus(response.data);
      } catch (error) {
        console.error("Error checking subscription:", error);
      } finally {
        setCheckingSubscription(false);
      }
    };
    checkSubscription();
  }, [slug]);

  useEffect(() => {
    if (!user) {
      toast.error("Sessão expirada. Por favor, faça login novamente.");
      navigate(`/${slug}/login`);
      return;
    }
    
    // Verify the logged-in user belongs to this empresa
    if (empresa && empresa.slug !== slug) {
      toast.error("Você está logado em outra empresa. Por favor, faça login novamente.");
      localStorage.removeItem("token");
      localStorage.removeItem("empresa");
      navigate(`/${slug}/login`);
      return;
    }
    
    fetchCasos();
  }, [user, slug, empresa]);

  const fetchCasos = async (statusParam = null) => {
    try {
      setLoading(true);
      
      // Build query params
      const params = statusParam && statusParam !== "all" ? `?status=${statusParam}` : "";
      
      const [denunciasRes, ouvidoriasRes] = await Promise.all([
        axios.get(`${API_URL}/empresas/${slug}/denuncias${params}`),
        axios.get(`${API_URL}/empresas/${slug}/ouvidoria${params}`),
      ]);
      setDenuncias(denunciasRes.data.denuncias || []);
      setOuvidorias(ouvidoriasRes.data.ouvidoria || []);
    } catch (error) {
      console.error("Error fetching casos:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error("Acesso negado. Você pode estar logado em outra empresa.");
        localStorage.removeItem("token");
        localStorage.removeItem("empresa");
        navigate(`/${slug}/login`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Refetch when status filter changes - handle backend vs frontend filters
  useEffect(() => {
    if (statusFilter === "sla_vencido") {
      // SLA filter uses backend filtering
      fetchCasos("sla_vencido");
      setLastBackendFilter("sla_vencido");
    } else if (lastBackendFilter === "sla_vencido") {
      // Coming from SLA filter - need to reload all data
      fetchCasos();
      setLastBackendFilter(null);
    }
    // Other filters (all, recebida, em_analise, etc.) use frontend filtering
    // No need to refetch, the data is already loaded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Download backup completo em ZIP
  const handleBackupDownload = async () => {
    try {
      toast.info("Gerando backup ZIP com todos os casos...");
      const response = await axios.get(
        `${API_URL}/empresas/${slug}/casos/backup-zip`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = `backup_${slug}_${new Date().toISOString().split('T')[0]}.zip`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Backup ZIP gerado com sucesso!");
    } catch (error) {
      console.error("Error downloading backup:", error);
      toast.error("Erro ao gerar backup");
    }
  };

  // Download PDF do caso
  const handlePdfDownload = async (casoId, tipo, protocolo) => {
    try {
      toast.info("Gerando PDF...");
      const response = await axios.get(
        `${API_URL}/empresas/${slug}/casos/${casoId}/pdf?tipo=${tipo}`,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_${protocolo || casoId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF gerado com sucesso!");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erro ao gerar PDF");
    }
  };

  const handleLogout = () => {
    logout();
    navigate(`/${slug}/login`);
  };

  // Combine and filter cases
  const allCasos = [
    ...denuncias.map(d => ({ ...d, tipo: "denuncia" })),
    ...ouvidorias.map(o => ({ ...o, tipo: "ouvidoria" })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const filteredCasos = allCasos.filter(caso => {
    // Type filter
    if (tipoFilter !== "all" && caso.tipo !== tipoFilter) return false;
    
    // Status filter - skip for sla_vencido since it's handled by backend
    if (statusFilter !== "all" && statusFilter !== "sla_vencido" && caso.status !== statusFilter) return false;
    
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        caso.protocolo.toLowerCase().includes(q) ||
        caso.descricao.toLowerCase().includes(q) ||
        (caso.categoria && caso.categoria.toLowerCase().includes(q)) ||
        (caso.nome && caso.nome.toLowerCase().includes(q))
      );
    }
    
    return true;
  });

  const getStatusLabel = (status) => {
    const labels = {
      recebida: t("caseDetails.statusLabels.received"),
      em_analise: t("caseDetails.statusLabels.in_progress"),
      investigacao: t("caseDetails.statusLabels.investigation"),
      concluida: t("caseDetails.statusLabels.concluded"),
      arquivada: t("caseDetails.statusLabels.archived"),
      respondida: t("caseDetails.statusLabels.answered"),
      fechada: t("caseDetails.statusLabels.closed"),
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      recebida: "bg-blue-100 text-blue-700",
      em_analise: "bg-yellow-100 text-yellow-700",
      investigacao: "bg-purple-100 text-purple-700",
      concluida: "bg-green-100 text-green-700",
      arquivada: "bg-slate-100 text-slate-700",
      respondida: "bg-green-100 text-green-700",
      fechada: "bg-slate-100 text-slate-700",
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  const getCategoriaLabel = (categoria) => {
    const labels = {
      fraude: t("caseDetails.categoryLabels.fraud"),
      assedio: t("caseDetails.categoryLabels.harassment"),
      seguranca: t("caseDetails.categoryLabels.safety"),
      corrupcao: t("caseDetails.categoryLabels.corruption"),
      outro: t("caseDetails.categoryLabels.other"),
      sugestao: t("caseDetails.ombudsmanTypes.suggestion"),
      reclamacao: t("caseDetails.ombudsmanTypes.complaint"),
      elogio: t("caseDetails.ombudsmanTypes.compliment"),
      informacao: t("caseDetails.ombudsmanTypes.information"),
      denuncia: t("cases.whistleblowing"),
      ouvidoria: t("cases.ombudsman"),
    };
    return labels[categoria] || categoria;
  };

  // Show loading while checking subscription
  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#f5a623] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Show trial expired screen if no access
  if (subscriptionStatus && !subscriptionStatus.has_access) {
    return <TrialExpiredScreen empresa={empresa} slug={slug} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {empresa?.logo_url ? (
                  <img src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)} alt="" className="h-10 w-auto" />
                ) : (
                  <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="font-semibold text-[#1a1a2e] text-sm">{empresa?.nome}</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </div>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            <Link to={`/${slug}/admin`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
              <LayoutDashboard className="w-5 h-5" />
              {t("dashboard.menu.dashboard")}
            </Link>
            <Link to={`/${slug}/admin/casos`} className="flex items-center gap-3 px-4 py-3 text-[#1a1a2e] bg-slate-100 rounded-lg font-medium">
              <FileText className="w-5 h-5" />
              {t("dashboard.menu.cases")}
            </Link>
            {(user?.role === "admin" || user?.role === "gestor") && (
              <Link to={`/${slug}/admin/investigadores`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
                <Users className="w-5 h-5" />
                {t("dashboard.menu.investigators")}
              </Link>
            )}
            {user?.role === "admin" && (
              <Link to={`/${slug}/admin/configuracoes`} className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
                <Settings className="w-5 h-5" />
                {t("dashboard.menu.settings", "Configurações")}
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                {user?.nome?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t("dashboard.menu.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-[#1a1a2e]">{t("cases.title")}</h1>
          </div>
        </header>

        <div className="flex-1 p-6">
          {/* Filters */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-600">{t("common.filters")}:</span>
              </div>
              
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("cases.searchPlaceholder")}
                  className="pl-10"
                />
              </div>

              <Select value={tipoFilter} onValueChange={setTipoFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder={t("cases.type")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("cases.allTypes")}</SelectItem>
                  <SelectItem value="denuncia">{t("cases.whistleblowing")}</SelectItem>
                  <SelectItem value="ouvidoria">{t("cases.ombudsman")}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder={t("cases.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("cases.allStatus")}</SelectItem>
                  <SelectItem value="sla_vencido" data-testid="sla-vencido-option">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                      {t("cases.slaExpired")}
                    </span>
                  </SelectItem>
                  <SelectItem value="recebida">{t("caseDetails.statusLabels.received")}</SelectItem>
                  <SelectItem value="em_analise">{t("caseDetails.statusLabels.in_progress")}</SelectItem>
                  <SelectItem value="investigacao">{t("caseDetails.statusLabels.investigation")}</SelectItem>
                  <SelectItem value="concluida">{t("caseDetails.statusLabels.concluded")}</SelectItem>
                  <SelectItem value="respondida">{t("caseDetails.statusLabels.answered")}</SelectItem>
                  <SelectItem value="fechada">{t("caseDetails.statusLabels.closed")}</SelectItem>
                  <SelectItem value="arquivada">{t("caseDetails.statusLabels.archived")}</SelectItem>
                </SelectContent>
              </Select>

              {/* Backup Button - Admin only */}
              {user?.role === "admin" && (
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBackupDownload("zip")}
                    className="text-[#1a1a2e] border-[#1a1a2e] hover:bg-[#1a1a2e] hover:text-white"
                    data-testid="btn-backup-zip"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Backup (ZIP)
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Cases List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
            </div>
          ) : filteredCasos.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t("cases.noCasesFound")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCasos.map((caso) => (
                <Link
                  key={caso.id}
                  to={`/${slug}/admin/${caso.tipo === "denuncia" ? "denuncias" : "ouvidoria"}/${caso.id}`}
                  className="block bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        caso.tipo === "denuncia" ? "bg-[#1a1a2e]/10" : "bg-[#f5a623]/10"
                      }`}>
                        {caso.tipo === "denuncia" ? (
                          <AlertTriangle className="w-5 h-5 text-[#1a1a2e]" />
                        ) : (
                          <MessageSquare className="w-5 h-5 text-[#f5a623]" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold text-[#1a1a2e]">{caso.protocolo}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(caso.status)}`}>
                            {getStatusLabel(caso.status)}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-2">{caso.descricao}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="capitalize">{getCategoriaLabel(caso.categoria || caso.tipo)}</span>
                          <span>{new Date(caso.created_at).toLocaleDateString()}</span>
                          {caso.nome && <span>{caso.nome}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handlePdfDownload(caso.id, caso.tipo, caso.protocolo);
                        }}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Gerar PDF"
                        data-testid={`btn-pdf-${caso.id}`}
                      >
                        <FileText className="w-4 h-4 text-red-500" />
                      </button>
                      <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
